package gr.tech.site.data

/**
 * Three-tier visual ranking for the state card grid.
 * FEATURED  — state has a dedicated page; darkest green treatment.
 * ACTIVE    — established programs / notable grow market; medium green.
 * STANDARD  — all remaining states; lightest green.
 */
enum class StateTier { FEATURED, ACTIVE, STANDARD }

/**
 * Single source of truth for every state.
 *
 * [mapKey]   — lowercase 2-letter SVG CSS class. Null for Alaska & Hawaii
 *              (they are not rendered on the contiguous-US map).
 * [statePage] — dedicated route like "/states/michigan". Null if no page exists yet.
 * [utilities] — display names shown in the utility modal / contact form pre-fill.
 * [tier]     — card visual tier; defaults to STANDARD.
 */
data class StateInfo(
    val name: String,
    val abbr: String,
    val mapKey: String?,
    val statePage: String? = null,
    val utilities: List<String> = emptyList(),
    val favoriteUtilities: Set<String> = emptySet(),
    val tier: StateTier = StateTier.STANDARD
)

val allStates: List<StateInfo> = listOf(
    StateInfo("Alabama", "AL", "al"),
    StateInfo("Alaska", "AK", mapKey = null), // TODO: real utilities for Alaska (not on SVG map)

    StateInfo("Arizona", "AZ", "az"),
    StateInfo("Arkansas", "AR", "ar"),
    StateInfo("California", "CA", "ca", statePage = "/states/california", tier = StateTier.FEATURED, utilities = listOf(
        "SCE",
        "PG&E",
        "PSE",
        "SDGE"
    )),

    StateInfo("Colorado", "CO", "co"),
    StateInfo("Connecticut", "CT", "ct", tier = StateTier.ACTIVE),
    StateInfo("Delaware", "DE", "de"),
    StateInfo("Florida", "FL", "fl"),
    StateInfo("Georgia", "GA", "ga"),
    StateInfo("Hawaii", "HI", mapKey = null), // TODO: real utilities for Hawaii (not on SVG map)

    StateInfo("Idaho", "ID", "id"),
    StateInfo("Illinois", "IL", "il", tier = StateTier.ACTIVE),
    StateInfo("Indiana", "IN", "in"),
    StateInfo("Iowa", "IA", "ia"),
    StateInfo("Kansas", "KS", "ks"),
    StateInfo("Kentucky", "KY", "ky"),
    StateInfo("Louisiana", "LA", "la"),
    StateInfo("Maine", "ME", "me", tier = StateTier.ACTIVE),
    StateInfo("Maryland", "MD", "md"),
    StateInfo("Massachusetts", "MA", "ma", tier = StateTier.ACTIVE),
    StateInfo("Michigan", "MI", "mi", statePage = "/states/michigan", tier = StateTier.FEATURED, utilities = listOf(
        "Consumers",
        "DTE"
    )),

    StateInfo("Minnesota", "MN", "mn", statePage = "/states/minnesota", tier = StateTier.FEATURED, utilities = listOf(
        "Xcel MN"
    )),

    StateInfo("Mississippi", "MS", "ms"),
    StateInfo("Missouri", "MO", "mo"),
    StateInfo("Montana", "MT", "mt", tier = StateTier.ACTIVE),
    StateInfo("Nebraska", "NE", "ne"),
    StateInfo("Nevada", "NV", "nv", utilities = listOf(
        "NV Energy"
    )),

    StateInfo("New Hampshire", "NH", "nh"),
    StateInfo("New Jersey", "NJ", "nj", tier = StateTier.ACTIVE, utilities = listOf(
        "NJCEP"
    )),

    StateInfo("New Mexico", "NM", "nm"),
    StateInfo("New York", "NY", "ny", tier = StateTier.ACTIVE, utilities = listOf(
        "NYSEG"
    )),

    StateInfo("North Carolina", "NC", "nc"),
    StateInfo("North Dakota", "ND", "nd"),
    StateInfo("Ohio", "OH", "oh", tier = StateTier.ACTIVE),
    StateInfo("Oklahoma", "OK", "ok", utilities = listOf(
        "PSO"
    )),

    StateInfo("Oregon", "OR", "or", statePage = "/states/oregon", tier = StateTier.FEATURED, utilities = listOf(
        "Energy Trust of Oregon"
    )),

    StateInfo("Pennsylvania", "PA", "pa", tier = StateTier.ACTIVE),
    StateInfo("Rhode Island", "RI", "ri"),
    StateInfo("South Carolina", "SC", "sc"),
    StateInfo("South Dakota", "SD", "sd"),
    StateInfo("Tennessee", "TN", "tn"),
    StateInfo("Texas", "TX", "tx"),
    StateInfo("Utah", "UT", "ut"),
    StateInfo("Vermont", "VT", "vt", tier = StateTier.ACTIVE),
    StateInfo("Virginia", "VA", "va"),
    StateInfo("Washington", "WA", "wa", statePage = "/states/washington", tier = StateTier.FEATURED, utilities = listOf(
        "Avista",
        "Benton REA",
        "Big Bend Electric",
        "Clallam County PUD",
        "Clark County PUD",
        "Cowlitz County",
        "Grant County PUD",
        "Inland Power",
        "Jefferson County PUD",
        "Kittitas PUD",
        "Klickitat PUD",
        "Lewis County",
        "Mason County PUD",
        "Okanogan County PUD",
        "Pacific County PUD",
        "Pacific Power",
        "PSE",
        "SCL",
        "Skamania PUD",
        "SnoPUD",
        "Tacoma Power",
        "USDA Program",
        "Vera Irrigation District #15"
    ), favoriteUtilities = setOf("PSE", "SCL", "SnoPUD", "Avista", "Tacoma Power")),

    StateInfo("West Virginia", "WV", "wv"),
    StateInfo("Wisconsin", "WI", "wi", tier = StateTier.ACTIVE),
    StateInfo("Wyoming", "WY", "wy")
)

/** mapKey → statePage; used by UsStateMap to decide whether to navigate on click */
val statePageRoutes: Map<String, String> = allStates
    .filter { it.statePage != null && it.mapKey != null }
    .associate { it.mapKey!! to it.statePage!! }

/** All mapKeys present in us-map.svg (contiguous US, excludes AK + HI) */
val allMapStateKeys: Set<String> = allStates
    .mapNotNull { it.mapKey }
    .toSet()

fun getStateInfo(stateString: String): StateInfo {
    return allStates.first { it.name.equals(stateString, ignoreCase = true) }
}